import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

const ALL_CLASSES = [0, 1, 2, 3, 4];

export default function TimetableSlotEditModal({ mode, slot, presetPosition, onClose, onSaved }) {
  const isEdit = mode === 'edit';

  const [position, setPosition]   = useState(() => isEdit ? slot.position : (presetPosition || 1));
  const [timeStart, setTimeStart] = useState(() => isEdit ? slot.timeStart : '08:45');
  const [timeEnd,   setTimeEnd]   = useState(() => isEdit ? slot.timeEnd   : '09:25');
  const [allClasses, setAllClasses] = useState(() => isEdit ? slot.classNumbers === null : true);
  const [classes,   setClasses]   = useState(() => isEdit && Array.isArray(slot.classNumbers) ? new Set(slot.classNumbers) : new Set(ALL_CLASSES));

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const toggleClass = (n) => {
    setClasses((cur) => {
      const next = new Set(cur);
      if (next.has(n)) next.delete(n);
      else next.add(n);
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (timeEnd <= timeStart) { setError('Окончание должно быть позже начала'); return; }
    let classNumbers = null;
    if (!allClasses) {
      const arr = Array.from(classes).sort((a, b) => a - b);
      if (arr.length === 0) { setError('Выберите хотя бы один класс или включите «все»'); return; }
      classNumbers = arr;
    }

    setSaving(true);
    try {
      const payload = { position: Number(position), timeStart, timeEnd, classNumbers };
      const res = isEdit
        ? await api.put('/api/timetable-slots/' + slot.id, payload)
        : await api.post('/api/timetable-slots', payload);
      onSaved?.(res.slot);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err.message || 'Не удалось сохранить'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{isEdit ? `Слот — урок ${slot.position}` : 'Новый слот'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>
        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field">
              <label className="sd-edit__label">Номер урока *</label>
              <input
                className="sd-edit__input"
                type="number" min="1" max="20"
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                required
                autoFocus
              />
              <div className="sd-edit__hint">Один и тот же номер может иметь несколько вариантов времени для разных классов.</div>
            </div>
            <div className="sd-edit__field" />

            <div className="sd-edit__field">
              <label className="sd-edit__label">Начало *</label>
              <input className="sd-edit__input" type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} required />
            </div>
            <div className="sd-edit__field">
              <label className="sd-edit__label">Окончание *</label>
              <input className="sd-edit__input" type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} required />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Классы</label>
              <label className="staff-auth-row" style={{ marginBottom: 6 }}>
                <input
                  type="checkbox"
                  checked={allClasses}
                  onChange={(e) => setAllClasses(e.target.checked)}
                />
                <span>Все классы (0–4)</span>
              </label>
              {!allClasses && (
                <div className="tt-class-picker">
                  {ALL_CLASSES.map((n) => (
                    <label key={n} className={'tt-class-picker__chip' + (classes.has(n) ? ' is-on' : '')}>
                      <input
                        type="checkbox"
                        checked={classes.has(n)}
                        onChange={() => toggleClass(n)}
                      />
                      <span>{n} класс</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (isEdit ? 'Сохранить' : 'Создать')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
